import Box from '@mui/material/Box';
import Typography from '@mui/material/Typography';
import BugReportIcon from '@mui/icons-material/BugReport';
import CalendarTodayIcon from '@mui/icons-material/CalendarToday';
import GetAppIcon from '@mui/icons-material/GetApp';
import WarningIcon from '@mui/icons-material/Warning';
import type { FC } from 'react';
import { useCallback, useMemo, useState } from 'react';
import { Helmet } from 'react-helmet-async';
import { useNavigate } from 'react-router';
import { Button, Table } from 'src/common';
import { getPageTitle } from 'src/config/projectConfig';
import { SECURITY_PAGE_FILTER_KEYS } from 'src/mindtime/constants/securityFilters';
import {
  getSecurityMalwareDetailHref,
  getSecurityNavItemPath,
  SECURITY_ENDPOINTS_HREF,
} from 'src/mindtime/config/securityNav';
import { useSecurityEdrToolbarReset, useSecurityPageFilters } from 'src/mindtime/hooks/useSecurityPageFilters';
import {
  useWazuhScope,
  WAZUH_DATE_RANGE_OPTIONS,
  type WazuhDateRangeOption,
} from 'src/mindtime/hooks/useWazuhScope';
import type { Column, TableData } from 'src/types/table';
import type { WazuhMalwareDetectionItem } from 'src/types/mindtime/wazuhMalware';
import {
  EDR_COLORS,
  EdrDataCard,
  EdrDataToolbar,
  EdrDateTime,
  EdrKpiCard,
  EdrMalwareCharts,
  EdrPageHeader,
  EdrSeverityPill,
  EdrStatusPill,
  EdrTruncatedText,
  edrMonoStyle,
  edrMutedStyle,
  type EdrToolbarFilter,
} from '../shared/edrPrimitives';
import {
  getMalwareDetectionId,
  malwareSeverityToLevel,
  toMalwareTitle,
} from './malwareDetailModel';
import { useMalwareData, useMalwareExport } from './useMalwareData';

const breadcrumb = {
  links: [
    { name: 'EDR', to: SECURITY_ENDPOINTS_HREF },
    { name: 'Malware Detection', to: getSecurityNavItemPath('malware-detection') ?? '' },
  ],
};

const SEVERITY_OPTIONS = [
  { label: 'Critical', value: 'critical' },
  { label: 'High', value: 'high' },
  { label: 'Medium', value: 'medium' },
  { label: 'Low', value: 'low' },
];

const DATE_RANGE_OPTIONS = WAZUH_DATE_RANGE_OPTIONS.map((option) => ({
  label: option,
  value: option,
}));

const formatNumber = (value: number | undefined | null): string => {
  if (typeof value !== 'number' || Number.isNaN(value)) return '—';
  return value.toLocaleString();
};

const MalwareDetection: FC = () => {
  const navigate = useNavigate();
  const { dateRange, setDateRange, isReseller } = useWazuhScope();
  const { filters, setFilter, search, searchInput, setSearchInput, resetFilters } =
    useSecurityPageFilters(SECURITY_PAGE_FILTER_KEYS.malwareDetection);
  const {
    severityBand = '',
    malwareType = '',
    organizationId = '',
    wazuhAgentId = '',
  } = filters;
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(10);

  const malwareFilters = useMemo(
    () => ({
      severityBand: filters.severityBand,
      malwareType: filters.malwareType,
      wazuhAgentId: filters.wazuhAgentId,
      organizationId: filters.organizationId,
    }),
    [filters]
  );

  const openMalwareDetail = useCallback(
    (item: WazuhMalwareDetectionItem) => {
      const id = getMalwareDetectionId(item);
      if (!id) return;
      navigate(getSecurityMalwareDetailHref(id), {
        state: { record: item },
      });
    },
    [navigate]
  );

  const { loading, summary, items: paged, categories, facets } = useMalwareData(
    page,
    rowsPerPage,
    search,
    malwareFilters
  );
  const { exporting, handleExport } = useMalwareExport(search, malwareFilters);

  const items = paged?.items ?? [];
  const totalRecords = paged?.totalRecords ?? 0;

  const handleResetFilters = useSecurityEdrToolbarReset(resetFilters, setDateRange, setPage);

  const toolbarFilters: EdrToolbarFilter[] = useMemo(
    () => [
      {
        id: 'severity',
        label: 'Severity',
        icon: <WarningIcon style={{ fontSize: 14 }} />,
        options: SEVERITY_OPTIONS,
        value: severityBand,
        onChange: (value) => {
          setFilter('severityBand', value);
          setPage(0);
        },
      },
      {
        id: 'type',
        label: 'Malware Type',
        options: facets.types.map((name) => ({ label: name, value: name })),
        value: malwareType,
        onChange: (value) => {
          setFilter('malwareType', value);
          setPage(0);
        },
      },
      ...(isReseller
        ? [
          {
            id: 'org',
            label: 'Organization',
            variant: 'resellerClient' as const,
            value: organizationId,
            onChange: (value: string) => {
              setFilter('organizationId', value);
              setFilter('wazuhAgentId', '');
              setPage(0);
            },
          },
        ]
        : []),
      {
        id: 'endpoint',
        label: 'Endpoint',
        variant: 'searchableEndpoint' as const,
        pageOrganizationId: organizationId || undefined,
        value: wazuhAgentId,
        onChange: (value: string) => {
          setFilter('wazuhAgentId', value);
          setPage(0);
        },
      },
      {
        id: 'date',
        label: 'Date Range',
        icon: <CalendarTodayIcon style={{ fontSize: 14 }} />,
        options: DATE_RANGE_OPTIONS,
        value: dateRange,
        onChange: (value) => {
          setDateRange(value as WazuhDateRangeOption);
          setPage(0);
        },
      },
    ],
    [
      severityBand,
      malwareType,
      organizationId,
      wazuhAgentId,
      dateRange,
      isReseller,
      facets.types,
      setFilter,
      setDateRange,
    ]
  );

  const columns: Column[] = useMemo(
    () => [
      {
        id: 'threatName',
        label: 'Threat Name',
        width: '35%',
        render: (row) => (
          <EdrTruncatedText
            value={(row as unknown as WazuhMalwareDetectionItem).threatName || '—'}
            style={{ fontWeight: 600, color: EDR_COLORS.textPrimary }}
            maxWidth={470}
          />
        ),
      },
      {
        id: 'malwareType',
        label: 'Malware Type',
        width: '13%',
        render: (row) => (
          <EdrTruncatedText
            value={(row as unknown as WazuhMalwareDetectionItem).malwareType || '—'}
            style={edrMutedStyle}
            maxWidth="100%"
          />
        ),
      },
      {
        id: 'endpoint',
        label: 'Endpoint',
        width: '13%',
        render: (row) => (
          <EdrTruncatedText
            value={(row as unknown as WazuhMalwareDetectionItem).endpoint || '—'}
            style={edrMonoStyle}
            maxWidth="100%"
          />
        ),
      },
      {
        id: 'organization',
        label: 'Organization',
        width: '15%',
        render: (row) => (
          <EdrTruncatedText
            value={(row as unknown as WazuhMalwareDetectionItem).organization || '—'}
            style={edrMutedStyle}
            maxWidth="100%"
          />
        ),
      },
      {
        id: 'severity',
        label: 'Severity',
        width: '12%',
        render: (row) => {
          const item = row as unknown as WazuhMalwareDetectionItem;
          return (
            <EdrSeverityPill
              level={malwareSeverityToLevel(item.severity)}
              label={toMalwareTitle(item.severity) || '—'}
            />
          );
        },
      },
      {
        id: 'timestamp',
        label: 'Detection Time',
        width: '15%',
        render: (row) => (
          <EdrDateTime value={(row as unknown as WazuhMalwareDetectionItem).timestamp} />
        ),
      },
    ],
    []
  );

  const totalDetections = summary?.totalDetections;
  const criticalThreats = summary?.criticalThreats;
  const detectionsToday = summary?.detectionsToday;

  return (
    <>
      <Helmet>
        <title>{getPageTitle('Security — Malware Detection')}</title>
      </Helmet>

      <Box
        p={3}
        display="flex"
        flexDirection="column"
        style={{ gap: 16, minHeight: '100%', boxSizing: 'border-box' }}
      >
        <EdrPageHeader
          breadcrumb={breadcrumb}
          title="Malware Detection"
          subtitle="Monitor, investigate, and respond to malware detections across all protected endpoints."
          actions={(
            <Button
              variant="contained"
              color="primary"
              startIcon={<GetAppIcon />}
              style={{ textTransform: 'none' }}
              loading={exporting}
              onClick={handleExport}
            >
              Export
            </Button>
          )}
        />
        {/* Scan Endpoint - no backend API yet */}

        <Box
          display="grid"
          style={{ gridTemplateColumns: 'minmax(0, 1fr) minmax(0, 1fr)', gap: 16, alignItems: 'stretch' }}
        >
          <Box display="flex" flexDirection="column" style={{ gap: 16, minHeight: 0 }}>
            <EdrKpiCard
              label="Total Detections"
              value={formatNumber(totalDetections)}
              icon={<BugReportIcon style={{ fontSize: 18 }} />}
              iconColor={EDR_COLORS.critical}
              accent={EDR_COLORS.critical}
            >
              {typeof criticalThreats === 'number' ? (
                <EdrStatusPill
                  tone="critical"
                  label={`${formatNumber(criticalThreats)} Critical`}
                  dot={false}
                />
              ) : (
                <EdrStatusPill tone="neutral" label="Across all monitored endpoints" dot={false} />
              )}
            </EdrKpiCard>
            <EdrKpiCard
              label="Detections Today"
              value={formatNumber(detectionsToday)}
              icon={<CalendarTodayIcon style={{ fontSize: 18 }} />}
              iconColor={EDR_COLORS.high}
              accent={EDR_COLORS.high}
            >
              <EdrStatusPill tone="high" label="New detections in the last 24 hours" dot={false} />
            </EdrKpiCard>
          </Box>

          <EdrMalwareCharts categories={categories?.categories ?? null} />
        </Box>

        <EdrDataCard
          toolbar={(
            <EdrDataToolbar
              searchValue={searchInput}
              onSearchChange={(v) => {
                setSearchInput(v);
                setPage(0);
              }}
              searchPlaceholder="Search Threats…"
              filters={toolbarFilters}
              onReset={handleResetFilters}
            />
          )}
        >
          <Table
            columns={columns}
            data={items as unknown as TableData[]}
            loading={loading}
            showPagination
            page={page}
            rowsPerPage={rowsPerPage}
            totalRows={totalRecords}
            onPageChange={setPage}
            onRowsPerPageChange={(next) => {
              setRowsPerPage(next);
              setPage(0);
            }}
            onRowClick={(row) => openMalwareDetail(row as unknown as WazuhMalwareDetectionItem)}
            TableContainerProps={{ sx: { boxShadow: 'none', borderRadius: 0 } }}
          />
        </EdrDataCard>
      </Box>
    </>
  );
};

export default MalwareDetection;
